import { itemScopeApplies, type GeneratedStatus } from "@keydials/api-client";
import type { DialVM, VSection, VSegment } from "../dialModel";
import { segmentNoun } from "../vocabulary";
import { rollupAxisStatuses, sectionOwnPalette, sectionRollupAxisStatuses, sectionStatusPalette } from "../instanceModel";

/**
 * One row of the key under the dial.
 *
 * The key used to claim "None / Some / All complete" for every itemised dial, whatever had actually
 * coloured the segments — so a dial scored on a five-grade ladder showed its grades on the dial and
 * three completion words underneath. This picks the vocabulary the reader is actually looking at.
 *
 * `rag` is the honest answer for a dial that nothing else colours: there, the three bands really are
 * the whole vocabulary. It is not a "couldn't work it out" fallback — every other case names a
 * palette, because that palette is what is on screen.
 */
export type LegendScale =
  | { kind: "statuses"; title: string | null; statuses: GeneratedStatus[] }
  | { kind: "rag"; title: string | null };

const byOrder = (a: GeneratedStatus, b: GeneratedStatus) => a.order - b.order;

const sorted = (statuses: GeneratedStatus[]): GeneratedStatus[] => [...statuses].sort(byOrder);

/** The same key sequence twice is one scale said twice; a second row of it tells the reader nothing. */
function sameScale(a: GeneratedStatus[], b: GeneratedStatus[]): boolean {
  return a.length === b.length && a.every((s, i) => s.key === b[i]!.key);
}

/**
 * The scale(s) ONE action point speaks (see {@link scalesForSegment}).
 *
 * Mirrors `resolvedStatus`: a display rule resolves into the SEGMENT's palette and the axis rollup
 * (KEY-482) into the AXIS's, so whichever one actually answered for this segment is the one whose
 * words belong under the dial.
 */
/**
 * The scales of the item checks scoped to THIS action point (KEY-835): `appliesToItemKeys` non-empty and reaching it,
 * by the segment-wide entry or by one of its items. A dial-wide check (empty scope) is deliberately not here — it
 * covers everything, so it names nothing about this action point — and neither is the check a days section colours its
 * slots from, which {@link daySlotScale} already speaks. A list scored by two scoped checks yields both, in check order.
 */
function scopedCheckScales(vm: DialVM, sectionKey: string | null, segment: VSegment): GeneratedStatus[][] {
  if (!sectionKey) return [];
  const section = vm.detail.sections.find((s) => s.key === sectionKey);
  const raw = section?.segments.find((g) => g.key === segment.key);
  const slotCheck = section?.slotColouring?.dimensionKey;
  const itemKeys = (raw?.items ?? []).map((i) => i.key);
  const out: GeneratedStatus[][] = [];
  for (const d of vm.detail.dimensions ?? []) {
    if (!d.appliesToItemKeys?.length || d.key === slotCheck || d.palette.statuses.length === 0) continue;
    const reaches = itemScopeApplies(d.appliesToItemKeys, sectionKey, segment.key, "*")
      || itemKeys.some((k) => itemScopeApplies(d.appliesToItemKeys, sectionKey, segment.key, k));
    if (reaches && !out.some((o) => sameScale(o, sorted(d.palette.statuses)))) out.push(sorted(d.palette.statuses));
  }
  return out;
}

const asRows = (scales: GeneratedStatus[][]): LegendScale[] =>
  scales.map((statuses) => ({ kind: "statuses" as const, title: null, statuses }));

/** Every scale ONE action point speaks — usually one; two or more when it is scored by several scoped checks. */
function scalesForSegment(vm: DialVM, segment: VSegment): LegendScale[] {
  // A group wedge showing a child status (KEY-509) speaks its children's vocabulary, not the group's.
  if (segment.isGroupSegment) {
    const shown = groupStatusScale(vm);
    if (shown) {
      return [shown];
    }
  }
  // KEY-771: a time of day on a days section coloured from its items speaks its check's scale — empty or not. Its
  // own palette (the dial default, usually busyness) never colours it, so naming that would be the wrong key.
  const slot = daySlotScale(vm, segment);
  if (slot) {
    return [{ kind: "statuses", title: null, statuses: sorted(slot) }];
  }
  const scoped = scopedCheckScales(vm, sectionKeyOf(vm, segment), segment);
  // Not scored by items: set by hand, out of its own palette (its override, or the default). Unless a scoped check
  // owns this list and nothing was set by hand — then the dial default would name a scale the list never uses.
  if (!segment.itemActive) {
    if (scoped.length > 0 && !segment.displayStatus) {
      return asRows(scoped);
    }
    return [{ kind: "statuses", title: null, statuses: sorted(segment.palette) }];
  }

  const axis = rollupAxisStatuses(vm.detail);
  if (segment.displayStatus) {
    const key = segment.displayStatus.key;
    // Segment palette first, then the axis — the order `resolvedStatus` tries them in. Neither holding the key
    // means something else resolved it (a check's scale); name that rather than leave the key empty.
    const found = [segment.palette, axis, ...(vm.detail.dimensions ?? []).map((d) => d.palette.statuses)]
      .find((p) => p.some((s) => s.key === key));
    if (found) {
      // The list's own scoped checks, when the colour came from one of them: all of its scales, not just the first.
      if (scoped.length > 1 && scoped.some((o) => o.some((s) => s.key === key))) {
        return asRows(scoped);
      }
      return [{ kind: "statuses", title: null, statuses: sorted(found) }];
    }
  }
  // A list scored by its own item checks speaks them (KEY-835), ahead of the axis or the bare bands.
  if (scoped.length > 0) {
    return asRows(scoped);
  }
  // Nothing resolved yet, but an axis still governs where this segment's colour will land once
  // something does — naming it is more use than three words about completion.
  if (axis.length > 0) {
    return [{ kind: "statuses", title: null, statuses: sorted(axis) }];
  }
  // Otherwise the segment really is banded. Even a per-segment palette override only lends three of
  // its colours to the three bands (`bandPaletteColour`), so the vocabulary is still RAG.
  return [{ kind: "rag", title: null }];
}

/**
 * The SCALES a group's wedges speak (KEY-527) — the whole of each child palette in play, not just the
 * words currently painted — one row per distinct palette, or null when the server named none.
 *
 * `groupStatusScale` below is what this replaces, and why it had to: a group holds no palette its
 * children's statuses live in, so the only thing the client could assemble unaided was the set of
 * statuses ON SCREEN. On the common group whose children all sit at the same grade that is a key of one
 * word, which reads as "this dial has one status" rather than "everything here is currently Good".
 *
 * `onlySectionKey` narrows to one wedge's own scale, the same way `scaleForSegment` does for a normal
 * dial: a chosen action point is the thing the reader is looking at.
 *
 * A single palette stays UNTITLED — the overwhelmingly common case, and identical to what shipped before
 * — because labelling the only scale on screen with the sections it covers tells the reader nothing they
 * cannot see. Titles appear exactly when there is a choice to disambiguate.
 */
function groupPaletteScales(vm: DialVM, onlySectionKey?: string | null): LegendScale[] | null {
  const palettes = vm.detail.groupPalettes;
  if (!palettes || palettes.length === 0) {
    return null;
  }
  const relevant = onlySectionKey
    ? palettes.filter((p) => p.sectionKeys.includes(onlySectionKey))
    : palettes;
  if (relevant.length === 0) {
    return null;
  }
  if (relevant.length === 1) {
    return [{ kind: "statuses", title: null, statuses: sorted(relevant[0]!.palette.statuses) }];
  }
  // Section LABELS, not keys: a mirror face's keys are namespaced ("m-1a2b3c4d-safeguarding") and mean
  // nothing to a reader. An unknown key falls back to itself rather than vanishing, so a row is never
  // left unlabelled while its neighbours are named.
  const labelOf = (key: string) => vm.sections.find((s) => s.key === key)?.label ?? key;
  return relevant.map((p) => ({
    kind: "statuses" as const,
    title: p.sectionKeys.map(labelOf).join(", "),
    statuses: sorted(p.palette.statuses),
  }));
}

/** The section a wedge belongs to — the grain `groupPaletteScales` labels its rows at. */
function sectionKeyOf(vm: DialVM, segment: VSegment): string | null {
  return vm.sections.find((s) => s.segments.some((g) => g.key === segment.key))?.key ?? null;
}

/**
 * The statuses a group's wedges are actually showing (KEY-509), or null when they colour by band. Listed
 * rather than looked up: the children may each grade on their own scale, and the group's own palette
 * holds none of them — so the key is the set of statuses on screen, in the order the ring shows them.
 *
 * Still the fallback beneath {@link groupPaletteScales} for a payload that names no group palettes — an
 * older server, or a group nothing has coloured yet.
 */
function groupStatusScale(vm: DialVM): LegendScale | null {
  const shown = new Map<string, GeneratedStatus>();
  for (const segment of vm.sections.flatMap((s) => s.segments)) {
    if (segment.isGroupSegment && segment.displayStatus && !shown.has(segment.displayStatus.key)) {
      shown.set(segment.displayStatus.key, segment.displayStatus);
    }
  }
  return shown.size > 0 ? { kind: "statuses", title: null, statuses: [...shown.values()] } : null;
}

/** The scale the dial's action points speak as a whole, with nothing selected. */
function dialSegmentScale(vm: DialVM): LegendScale {
  if (vm.isGroup) {
    const shown = groupStatusScale(vm);
    if (shown) {
      return shown;
    }
  }
  if (vm.simple) {
    return { kind: "statuses", title: null, statuses: sorted(vm.detail.statusSet.statuses) };
  }

  const axis = rollupAxisStatuses(vm.detail);
  // Naming the axis just because the dial has one would be wrong: `resolvedStatus` tries the SEGMENT's
  // palette first, so a dial that names an axis and also carries display rules is coloured out of its
  // segments' palettes, not the axis's. Ask what actually answered, the same way one segment does.
  const resolved = vm.sections.flatMap((s) => s.segments).filter((g) => g.displayStatus);
  if (resolved.length > 0) {
    const fromSegmentPalette = resolved.some((g) => g.palette.some((s) => s.key === g.displayStatus!.key));
    // Segments can each override the palette, so there is no single per-segment answer for the whole
    // dial — the definition default is the one they all fall back to, and choosing an action point
    // gives its exact scale.
    return {
      kind: "statuses",
      title: null,
      statuses: sorted(fromSegmentPalette ? vm.detail.statusSet.statuses : axis),
    };
  }
  // Nothing resolved yet: an axis still governs where the colours will land once something does.
  if (axis.length > 0) {
    return { kind: "statuses", title: null, statuses: sorted(axis) };
  }
  return { kind: "rag", title: null };
}

/**
 * KEY-795: the scales of sections that colour their edge in their OWN colours, one row per distinct palette. A days
 * section is one row however many days it shows — every day shares the template's palette.
 */
function ownSectionScales(vm: DialVM): LegendScale[] {
  const detail = vm.detail;
  const rows = new Map<string, { titles: string[]; statuses: GeneratedStatus[] }>();
  for (const section of detail.sections) {
    const source = section.colouring?.source;
    const own = source === "Rule" || source === "HandSet" ? sectionOwnPalette(detail, section.key) : undefined;
    if (!own) {
      continue;
    }
    const id = own.map((s) => `${s.key}:${s.colour}:${s.name}`).join("|");
    const title = section.kind === "Days" ? "Each day" : section.label;
    const row = rows.get(id);
    if (row) {
      if (!row.titles.includes(title)) row.titles.push(title);
    } else {
      rows.set(id, { titles: [title], statuses: own });
    }
  }
  const list = [...rows.values()];
  return list.map((r) => ({
    kind: "statuses" as const,
    title: list.length === 1 ? "Sections" : r.titles.join(", "),
    statuses: r.statuses,
  }));
}

/** Whether every section says what its edge shows (KEY-795) — then the dial-wide section scale describes none. */
function everySectionChoosesItsEdge(vm: DialVM): boolean {
  const sections = vm.detail.sections;
  return sections.length > 0 && sections.every((s) => s.colouring && s.colouring.source !== "Auto" && s.colouring.source !== "Busyness");
}

/** The scale the section edges speak, or null when this dial draws no section edge at all. */
function dialSectionScale(vm: DialVM): GeneratedStatus[] | null {
  const detail = vm.detail;
  if (everySectionChoosesItsEdge(vm)) {
    return null;
  }
  // An edge appears when a section status is hand-set (KEY-479) or derived from an axis (KEY-482/485).
  // Derivation is deliberately not gated on the hand-set master switch, exactly as `sectionStatusOf`.
  const derives = sectionRollupAxisStatuses(detail).length > 0;
  // KEY-771 part B: a day coloured from its items draws its busyness on the edge, in the dial's default palette.
  // KEY-795: only where busyness is what the edge shows — a section with its own rule keeps its busy bands unseen.
  const showsBusyness = (s: (typeof detail.sections)[number]) =>
    !s.colouring || s.colouring.source === "Auto" || s.colouring.source === "Busyness";
  if (!detail.sectionStatusEnabled && !derives && detail.sections.some((s) => showsBusyness(s) && (s.edgeStatusKey || s.segments.some((g) => g.edgeStatusKey)))) {
    return sorted(detail.statusSet.statuses);
  }
  if (!detail.sectionStatusEnabled && !derives) {
    return null;
  }
  const statuses = sectionStatusPalette(detail);
  return statuses.length > 0 ? sorted(statuses) : null;
}

/** The scale a time of day is coloured in, when it sits on a days section that colours from its items (KEY-771). */
function daySlotScale(vm: DialVM, segment: VSegment): GeneratedStatus[] | null {
  for (const section of vm.detail.sections) {
    const raw = section.segments.find((g) => g.key === segment.key);
    if (!raw) continue;
    const checkKey = section.slotColouring?.dimensionKey;
    if (!checkKey || !raw.date) return null;
    const statuses = vm.detail.dimensions?.find((d) => d.key === checkKey)?.palette.statuses ?? [];
    return statuses.length > 0 ? statuses : null;
  }
  return null;
}

/** A scale's identity for de-duplicating rows: two sections sharing one palette are one row, said once. */
function scaleId(scale: LegendScale): string {
  return scale.kind === "rag" ? "rag" : scale.statuses.map((s) => `${s.key}:${s.colour}:${s.name}`).join("|");
}

/** The edge one section draws, in its scale — or null when it draws none. */
function sectionEdgeScale(vm: DialVM, sectionKey: string): GeneratedStatus[] | null {
  const detail = vm.detail;
  const section = detail.sections.find((s) => s.key === sectionKey);
  if (!section || section.colouring?.source === "None") {
    return null;
  }
  const own = sectionOwnPalette(detail, sectionKey);
  if (own) {
    return own;
  }
  if (detail.sectionStatusEnabled || sectionRollupAxisStatuses(detail).length > 0) {
    const statuses = sectionStatusPalette(detail);
    return statuses.length > 0 ? sorted(statuses) : null;
  }
  // KEY-771 part B: a day coloured from its items draws its busyness on the edge, in the dial's default palette.
  const showsBusyness = !section.colouring || section.colouring.source === "Auto" || section.colouring.source === "Busyness";
  if (showsBusyness && (section.edgeStatusKey || section.segments.some((g) => g.edgeStatusKey))) {
    return sorted(detail.statusSet.statuses);
  }
  return null;
}

/**
 * The distinct scales a set of action points speak, one row each, titled by where they are used. A section whose
 * action points all share one scale is named once; one that mixes them names each action point.
 */
function pointScales(vm: DialVM, sections: VSection[]): { scale: LegendScale; titles: string[] }[] {
  const rows = new Map<string, { scale: LegendScale; titles: string[] }>();
  for (const section of sections) {
    const raw = vm.detail.sections.find((s) => s.key === section.key);
    const scales = section.segments.flatMap((g) => scalesForSegment(vm, g).map((scale) => ({ segment: g, scale })));
    const mixed = new Set(section.segments.map((g) => scalesForSegment(vm, g).map(scaleId).join("||"))).size > 1;
    for (const { segment, scale } of scales) {
      const title = raw?.kind === "Days" ? segmentNoun(vm.detail, section.key).Many : mixed ? segment.name : section.label;
      const id = scaleId(scale);
      const row = rows.get(id);
      if (row) {
        if (!row.titles.includes(title)) row.titles.push(title);
      } else {
        rows.set(id, { scale, titles: [title] });
      }
    }
  }
  // A long run of names helps nobody: three, then a count.
  const list = [...rows.values()].map((r) => ({
    ...r,
    titles: r.titles.length > 3 ? [...r.titles.slice(0, 3), `${r.titles.length - 3} more`] : r.titles,
  }));
  // RAG is the "nothing else colours it" answer; beside a real scale it is noise, not a vocabulary.
  const named = list.filter((r) => r.scale.kind === "statuses" && r.scale.statuses.length > 0);
  return named.length > 0 ? named : list.filter((r) => r.scale.kind === "rag").slice(0, 1);
}

/** Rows for a selected action point: its own scale, then the edge its section draws when that differs. */
function selectedPointScales(vm: DialVM, segment: VSegment): LegendScale[] {
  const owns = scalesForSegment(vm, segment);
  const own = owns[0]!;
  const sectionKey = sectionKeyOf(vm, segment);
  const edge = sectionKey ? sectionEdgeScale(vm, sectionKey) : null;
  if (!edge || (owns.length === 1 && own.kind === "statuses" && sameScale(own.statuses, edge))) {
    return owns;
  }
  return [
    ...owns.map((o) => ({ ...o, title: owns.length === 1 ? segmentNoun(vm.detail, sectionKey).One : o.title })),
    { kind: "statuses", title: "Section", statuses: edge },
  ];
}

/** Rows for a selected section: the edge it draws, then every scale its action points speak. */
function selectedSectionScales(vm: DialVM, section: VSection): LegendScale[] {
  const points = pointScales(vm, [section]);
  const edge = sectionEdgeScale(vm, section.key);
  const noun = segmentNoun(vm.detail, section.key).Many;
  const pointRows: LegendScale[] = points.map((p) => ({
    ...p.scale,
    title: points.length === 1 ? noun : p.titles.join(", "),
  }));
  if (!edge) {
    return points.length === 1 ? [{ ...points[0]!.scale, title: null }] : pointRows;
  }
  if (points.length === 1 && points[0]!.scale.kind === "statuses" && sameScale(points[0]!.scale.statuses, edge)) {
    return [{ ...points[0]!.scale, title: null }];
  }
  return [{ kind: "statuses", title: "Section", statuses: edge }, ...pointRows];
}

/**
 * The rows of the key under the dial: the scale in use, and what each row is for when a dial's
 * sections and action points speak different ones.
 */
export function legendScales(vm: DialVM, selSegment?: VSegment | null, selSection?: VSection | null): LegendScale[] {
  // A chosen action point is the thing the reader is looking at, so the key answers about that one.
  // The dial-wide scale would be answering a question they have just navigated away from.
  if (selSegment) {
    const group = vm.isGroup ? groupPaletteScales(vm, sectionKeyOf(vm, selSegment)) : null;
    return group ?? selectedPointScales(vm, selSegment);
  }
  if (selSection && !vm.isGroup) {
    return selectedSectionScales(vm, selSection);
  }

  // A group can speak SEVERAL scales at once — its children's — so the action-point key is a list here
  // where every other dial has exactly one row (KEY-527). Any other dial lists each scale its action points
  // speak, titled by where, once there is more than one.
  const spoken = vm.isGroup ? null : pointScales(vm, vm.sections);
  const points = (vm.isGroup ? groupPaletteScales(vm) : null)
    ?? (spoken && spoken.length > 1
      ? spoken.map((p) => ({ ...p.scale, title: p.titles.join(", ") }))
      : [spoken?.[0]?.scale ?? dialSegmentScale(vm)]);
  const sections = dialSectionScale(vm);
  // KEY-795: sections in their own colours always get their own row — their scale is never the action points'.
  const own = ownSectionScales(vm);
  if (own.length > 0) {
    return [
      ...points.map((p) => ({ ...p, title: p.title ?? segmentNoun(vm.detail).Many })),
      ...(sections ? [{ kind: "statuses" as const, title: own.length === 1 ? "Other sections" : "Sections", statuses: sections }] : []),
      ...own,
    ];
  }
  if (!sections) {
    return points;
  }
  const only = points.length === 1 ? points[0]! : null;
  if (only?.kind === "statuses" && sameScale(only.statuses, sections)) {
    return points;
  }
  // Two vocabularies on one dial (KEY-485 lets the sections roll up a different axis), so each row
  // has to say which of them it is describing.
  return [
    // KEY-539: the dial's own word. No new argument needed — `vm.detail` carries the vocabulary, and a vm
    // built without it (every existing test fixture) falls back to the platform's word unchanged. A group's
    // rows are already titled with the sections they cover, and keep that: it is the more specific answer.
    ...points.map((p) => ({ ...p, title: p.title ?? segmentNoun(vm.detail).Many })),
    { kind: "statuses", title: "Sections", statuses: sections },
  ];
}
